<template>
  <div
    :class="[
      theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100',
      'rounded-xl shadow-lg border'
    ]"
  >
    <!-- Table Controls -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'px-4 py-3 border-b space-y-3'
      ]"
    >
      <!-- Search and Global Actions Row -->
      <div class="flex items-center justify-between">
        <!-- Search -->
        <div
          v-if="config.enableSearch"
          class="flex-1 max-w-md"
        >
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'border-gray-300 text-gray-700',
                'w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
              ]"
              :placeholder="config.searchPlaceholder || 'Search...'"
            />
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                class="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Slot for custom actions -->
        <div class="flex items-center space-x-2">
          <slot name="table-actions"></slot>
        </div>
      </div>

      <!-- Active Filters Display -->
      <div
        v-if="hasActiveFilters"
        class="flex flex-wrap gap-2"
      >
        <div
          v-for="(value, field) in activeFilters"
          :key="field"
          :class="[
            theme.isDark.value ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-700',
            'inline-flex items-center px-2 py-1 rounded-md text-sm'
          ]"
        >
          {{ getColumnLabel(field) }}: {{ value }}
          <button
            class="ml-1 text-blue-500 hover:text-blue-700"
            @click="removeFilter(field)"
          >
            ×
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table
        class="min-w-full divide-y"
        :class="[theme.isDark.value ? 'divide-gray-700' : 'divide-gray-200']"
      >
        <thead :class="[theme.isDark.value ? 'bg-gray-900' : 'bg-gray-50']">
          <tr>
            <th
              v-for="column in columns"
              :key="column.field"
              class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none table-cell-border"
              :class="[
                theme.isDark.value ? 'text-gray-300' : 'text-gray-500',
                sortField === column.field ? 'text-blue-600' : ''
              ]"
              @click="handleSort(column.field)"
            >
              <div class="flex items-center space-x-1">
                <span>{{ column.label }}</span>
                <span
                  v-if="column.sortable !== false"
                  class="flex flex-col"
                >
                  <svg
                    :class="[
                      'w-3 h-3 transform',
                      sortField === column.field && sortOrder === 'asc'
                        ? 'text-blue-600'
                        : 'text-gray-400'
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                  <svg
                    :class="[
                      'w-3 h-3 transform',
                      sortField === column.field && sortOrder === 'desc'
                        ? 'text-blue-600'
                        : 'text-gray-400'
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </span>
              </div>
            </th>
            <th
              v-if="$slots['row-actions']"
              class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider table-cell-border"
              :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-500']"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody
          :class="[theme.isDark.value ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200']"
        >
          <template v-if="processedData.length">
            <tr
              v-for="item in processedData"
              :key="getItemKey(item)"
              :class="[theme.isDark.value ? 'hover:bg-gray-700' : 'hover:bg-gray-50']"
            >
              <td
                v-for="column in columns"
                :key="column.field"
                class="px-6 py-4 whitespace-nowrap text-sm table-cell-border"
                :class="[theme.isDark.value ? 'text-gray-200' : 'text-gray-800', column.class]"
                :data-type="getColumnType(column, item[column.field])"
              >
                <template v-if="$slots[column.field]">
                  <slot
                    :name="column.field"
                    :item="item"
                    :value="item[column.field]"
                  ></slot>
                </template>
                <template v-else>
                  <template v-if="column.formatter">
                    {{ column.formatter(item[column.field], item) }}
                  </template>
                  <template v-else>
                    {{ item[column.field] }}
                  </template>
                </template>
              </td>
              <td
                v-if="$slots['row-actions']"
                class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right table-cell-border"
              >
                <slot
                  name="row-actions"
                  :item="item"
                ></slot>
              </td>
            </tr>
          </template>
          <tr v-else>
            <td
              :colspan="getColSpan"
              class="px-6 py-4 text-center"
              :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-500']"
            >
              {{ config.emptyMessage || 'No data available' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="config.enablePagination && totalPages > 1"
      :class="[
        theme.isDark.value ? 'border-gray-700' : 'border-gray-200',
        'px-4 py-3 border-t sm:px-6 flex items-center justify-between'
      ]"
    >
      <div class="flex-1 flex justify-between sm:hidden">
        <button
          :disabled="currentPage === 1"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
            'relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md'
          ]"
          @click="currentPage--"
        >
          Previous
        </button>
        <button
          :disabled="currentPage === totalPages"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
            'ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md'
          ]"
          @click="currentPage++"
        >
          Next
        </button>
      </div>
      <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-700', 'text-sm']">
            Showing
            <span class="font-medium">{{ startIndex + 1 }}</span>
            to
            <span class="font-medium">{{ Math.min(endIndex, filteredData.length) }}</span>
            of
            <span class="font-medium">{{ filteredData.length }}</span>
            results
          </p>
        </div>
        <div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              :disabled="currentPage === 1"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                'relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium'
              ]"
              @click="currentPage = 1"
            >
              <span class="sr-only">First</span>
              ⟪
            </button>
            <button
              :disabled="currentPage === 1"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                'relative inline-flex items-center px-2 py-2 border text-sm font-medium'
              ]"
              @click="currentPage--"
            >
              <span class="sr-only">Previous</span>
              ⟨
            </button>
            <template
              v-for="page in displayedPages"
              :key="page"
            >
              <button
                v-if="page !== '...'"
                :class="[
                  currentPage === page
                    ? theme.isDark.value
                      ? 'z-10 bg-blue-900 border-blue-600 text-blue-300'
                      : 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : theme.isDark.value
                      ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                ]"
                @click="currentPage = Number(page)"
              >
                {{ page }}
              </button>
              <span
                v-else
                :class="[
                  theme.isDark.value
                    ? 'border-gray-600 bg-gray-700 text-gray-400'
                    : 'border-gray-300 bg-white text-gray-700',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                ]"
              >
                ...
              </span>
            </template>
            <button
              :disabled="currentPage === totalPages"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                'relative inline-flex items-center px-2 py-2 border text-sm font-medium'
              ]"
              @click="currentPage++"
            >
              <span class="sr-only">Next</span>
              ⟩
            </button>
            <button
              :disabled="currentPage === totalPages"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                'relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium'
              ]"
              @click="currentPage = totalPages"
            >
              <span class="sr-only">Last</span>
              ⟫
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineProps, defineExpose, useSlots, inject } from 'vue'
import { useTheme } from '../utils/useTheme'

// Use theme via inject with a fallback
const theme = inject('theme', useTheme())

interface Column {
  field: string
  label: string
  sortable?: boolean
  formatter?: (value: any, row: any) => string
  class?: string | string[]
  searchable?: boolean
  filterable?: boolean
  type?: 'text' | 'number' | 'date' | 'boolean'
}

interface TableConfig {
  enableSearch?: boolean
  enableSort?: boolean
  enableFilter?: boolean
  searchPlaceholder?: string
  idField?: string
  emptyMessage?: string
  enablePagination?: boolean
  itemsPerPage?: number
  defaultSort?: {
    field: string
    order: 'asc' | 'desc'
  }
}

const props = defineProps<{
  data: any[]
  columns: Column[]
  config?: Partial<TableConfig>
}>()

const slots = useSlots()

const defaultConfig: TableConfig = {
  enableSearch: true,
  enableSort: true,
  enableFilter: true,
  searchPlaceholder: 'Search all fields...',
  idField: '_id',
  emptyMessage: 'No data available',
  enablePagination: true,
  itemsPerPage: 10
}

const config = computed(() => ({ ...defaultConfig, ...props.config }))

// Search
const searchQuery = ref('')

// Sorting
const sortField = ref(config.value.defaultSort?.field || '')
const sortOrder = ref<'asc' | 'desc'>(config.value.defaultSort?.order || 'asc')

// Filtering
const activeFilters = ref<Record<string, any>>({})

// Pagination
const currentPage = ref(1)
const itemsPerPage = computed(() => config.value.itemsPerPage || 10)

// Helper functions
const getItemKey = (item: any): string => {
  const idField = config.value.idField || '_id'
  return item[idField]?.toString() || Math.random().toString()
}

const getColSpan = computed(() => {
  return props.columns.length + (slots['row-actions'] ? 1 : 0)
})

// Add deep search helper function
const deepSearch = (obj: any, searchText: string): boolean => {
  if (!obj) return false

  // Convert to lower case for case-insensitive search
  searchText = searchText.toLowerCase()

  // If it's a string, check if it contains the search text
  if (typeof obj === 'string') {
    return obj.toLowerCase().includes(searchText)
  }

  // If it's a number or boolean, convert to string and check
  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return String(obj).toLowerCase().includes(searchText)
  }

  // If it's an array, search through all elements
  if (Array.isArray(obj)) {
    return obj.some((item) => deepSearch(item, searchText))
  }

  // If it's an object, search through all values
  if (typeof obj === 'object') {
    return Object.values(obj).some((value) => deepSearch(value, searchText))
  }

  return false
}

// Update the filteredData computed property
const filteredData = computed(() => {
  let result = [...props.data]

  // Apply search
  if (searchQuery.value && config.value.enableSearch) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((item) => {
      // If specific searchable columns are defined, only search those
      if (props.columns.some((col) => col.searchable === true)) {
        return props.columns.some((column) => {
          if (column.searchable === false) return false
          const value = item[column.field]
          return deepSearch(value, query)
        })
      }
      // Otherwise, perform deep search on the entire item
      return deepSearch(item, query)
    })
  }

  // Apply filters
  Object.entries(activeFilters.value).forEach(([field, value]) => {
    result = result.filter((item) => String(item[field]) === String(value))
  })

  // Apply sorting
  if (sortField.value && config.value.enableSort) {
    result.sort((a, b) => {
      const aVal = a[sortField.value]
      const bVal = b[sortField.value]
      if (aVal === bVal) return 0
      const comparison = aVal > bVal ? 1 : -1
      return sortOrder.value === 'asc' ? comparison : -comparison
    })
  }

  return result
})

// Pagination computed properties
const totalPages = computed(() => Math.ceil(filteredData.value.length / itemsPerPage.value))
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value)
const endIndex = computed(() => startIndex.value + itemsPerPage.value)
const processedData = computed(() => {
  if (!config.value.enablePagination) return filteredData.value
  return filteredData.value.slice(startIndex.value, endIndex.value)
})

const displayedPages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const range: (number | string)[] = []

  if (total <= 7) {
    for (let i = 1; i <= total; i++) range.push(i)
  } else {
    if (current <= 3) {
      for (let i = 1; i <= 5; i++) range.push(i)
      range.push('...', total)
    } else if (current >= total - 2) {
      range.push(1, '...')
      for (let i = total - 4; i <= total; i++) range.push(i)
    } else {
      range.push(1, '...')
      for (let i = current - 1; i <= current + 1; i++) range.push(i)
      range.push('...', total)
    }
  }

  return range
})

const hasActiveFilters = computed(() => Object.keys(activeFilters.value).length > 0)

// Methods
const handleSort = (field: string) => {
  if (!config.value.enableSort) return
  const column = props.columns.find((col) => col.field === field)
  if (column?.sortable === false) return

  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'asc'
  }
}

const getColumnLabel = (field: string) => {
  const column = props.columns.find((col) => col.field === field)
  return column?.label || field
}

const removeFilter = (field: string) => {
  const newFilters = { ...activeFilters.value }
  delete newFilters[field]
  activeFilters.value = newFilters
}

// Watch for changes in search/filter to reset pagination
watch([searchQuery, activeFilters], () => {
  currentPage.value = 1
})

// Expose methods and properties that might be needed by parent
defineExpose({
  currentPage,
  totalPages,
  activeFilters,
  searchQuery,
  sortField,
  sortOrder,
  resetFilters: () => {
    activeFilters.value = {}
    searchQuery.value = ''
    currentPage.value = 1
    sortField.value = config.value.defaultSort?.field || ''
    sortOrder.value = config.value.defaultSort?.order || 'asc'
  }
})

const getColumnType = (column: Column, value: any): string => {
  if (column.type) return column.type
  if (typeof value === 'number') return 'number'
  if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value))))
    return 'date'
  if (typeof value === 'boolean') return 'boolean'
  return 'text'
}
</script>

<style>
/* Table Container */
.min-w-full {
  width: 100%;
}

/* Cell Border Styles */
.table-cell-border {
  @apply border-b border-opacity-50 dark:border-gray-700 dark:border-opacity-50 border-gray-200;
}

/* Header Styles */
thead th {
  @apply sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 font-semibold tracking-wider py-3.5 px-5;
}

thead th:hover {
  @apply bg-gray-100 dark:bg-gray-700;
}

/* Typography */
thead th {
  @apply text-xs uppercase text-gray-500 dark:text-gray-400;
}

tbody td {
  @apply text-sm text-gray-700 dark:text-gray-300 py-3.5 px-5;
}

/* Row Hover Effect */
tbody tr:hover td {
  @apply bg-gray-50 dark:bg-gray-700;
}

/* Text Alignment Based on Content Type */
td[data-type='number'],
td[data-type='date'] {
  text-align: right;
}

td[data-type='text'] {
  text-align: left;
}

td[data-type='boolean'] {
  text-align: center;
}

/* Scrollable Table with Fixed Header */
.overflow-x-auto {
  @apply relative rounded-lg bg-white dark:bg-gray-800 shadow;
}

/* Loading and Empty States */
.empty-state,
.loading-state {
  @apply p-8 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800;
}

/* Input placeholder color */
input::placeholder {
  @apply text-gray-400 dark:text-gray-500;
}

/* Responsive Design */
@media (max-width: 640px) {
  thead th,
  tbody td {
    @apply py-2.5 px-3.5;
  }
}
</style>
